/**
 * P2P-2864: перебор iOS-диплинков по очереди — повторяет логику кнопки
 * «Открыть Сбер» (Душанбе Сити) на чекауте провайдера PAYGO (checkout.sbp.ink,
 * openIosSchemesInOrder в main-check.min.js, 21.09.2026):
 *   - первая ссылка — window.location.replace сразу, ещё в обработчике
 *     нажатия (Safari видит жест пользователя и открывает приложение без
 *     «Открыть в …?»);
 *   - остальные — через невидимый iframe, по одной раз в `intervalMs`;
 *   - страница ушла в фон (document.hidden) → приложение открылось, стоп;
 *   - через links.length * intervalMs + 1500 мс без открытия → onExhausted.
 * Страница при этом не перезагружается (в отличие от redirect.html до P2P-3065,
 * где каждая схема была отдельной загрузкой с шагом 50 мс; на iOS так не работало).
 */
export const IOS_SCHEME_INTERVAL_MS = 700;

export const openIosSchemesInOrder = (
  links: string[],
  onExhausted?: () => void,
  intervalMs: number = IOS_SCHEME_INTERVAL_MS,
): (() => void) => {
  const urls = links.map((link) => link.trim()).filter(Boolean);

  if (urls.length === 0) {
    onExhausted?.();
    return () => undefined;
  }

  let index = 0;
  let stopped = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let deadline: ReturnType<typeof setTimeout> | null = null;
  const frames: HTMLIFrameElement[] = [];

  const onVisibilityChange = () => {
    if (document.hidden) {
      stop(true);
    }
  };

  const stop = (appOpened: boolean) => {
    if (stopped) return;
    stopped = true;
    if (timer !== null) clearInterval(timer);
    if (deadline !== null) clearTimeout(deadline);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    frames.forEach((frame) => frame.remove());
    if (!appOpened) onExhausted?.();
  };

  const attemptNext = () => {
    if (stopped) return;
    if (index >= urls.length) {
      if (timer !== null) clearInterval(timer);
      return;
    }

    const url = urls[index++];

    if (index === 1) {
      window.location.replace(url);
      return;
    }

    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText =
      'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;border:0;';
    document.body.appendChild(frame);
    frames.push(frame);
    frame.src = url;
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  attemptNext();

  if (!stopped && index < urls.length) {
    timer = setInterval(attemptNext, intervalMs);
  }

  deadline = setTimeout(() => stop(false), urls.length * intervalMs + 1500);

  return () => stop(true);
};
